<template>
  <div class="member-register">
    <edit-page-header
      page="REGISTER"
      :is-date-visible="false"
    >
      <el-button
        class="light-gray"
        @click="clearAllData"
      >
        취소
      </el-button>
      <el-loading-button
        class="black"
        :is-loading="isLoading"
        data-test="button-member-register"
        @click="handleClickRegisterForm"
      >
        회원 등록
      </el-loading-button>
    </edit-page-header>


    <div class="row no-gutters mb-5">
      <div class="col-xs-12 col-md-5 col-lg-5">
        <div
          class="card-box mr-md-3"
          style="height: 100%;"
        >
          <h2>기본 정보</h2>

          <MemberRegisterForm
            ref="registerForm"
            v-model="registerForm"
          />

          <el-checkbox
            v-model="isPolicyChecked"
            data-test="checkbox-policy"
            class="policy-check"
            size="medium"
          >
            해당 회원으로부터 피플박스 앱 이용에 필요한
            <a
              class="policy-link"
              href="https://fiflfifl.com/terms-conditions-app"
              target="_blank"
            >
              서비스 이용약관
            </a>,
            <a
              class="policy-link"
              href="https://fiflfifl.com/privacypolicy"
              target="_blank"
            >
              개인정보 처리방침
            </a> 및
            <a
              class="policy-link"
              href="https://fiflfifl.com/terms-conditions-app-location"
              target="_blank"
            >
              위치기반 서비스 이용약관
            </a> 동의를 받았습니다.
          </el-checkbox>
        </div>
      </div>

      <div class="col-xs-12 col-md-7 col-lg-7 mt-4 mt-md-0">
        <div
          class="card-box ml-md-3"
          style="height: 100%;"
        >
          <h2>회원권 정보</h2>
          <div class="membership-form-switch">
            <span>
              회원권 정보 입력
              <popover
                placement="bottom-end"
                style="margin-left: 3px; margin-right: 12px;"
              >
                회원권의 모든 정보를 입력하면 회원 등록이 <br>
                완료됩니다. 회원권 정보를 입력하지 않을 경우 <br>
                회원이 '승인 대기' 상태로 등록됩니다.
              </popover>
            </span>
            <el-switch
              data-test="switch-membership-submit"
              v-model="isSubmitMembershipForm"
            />
          </div>

          <ChildMembershipForm
            ref="childMembershipForm"
            v-model="childMembershipForm"
            :disabled="!isSubmitMembershipForm"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EditPageHeader from '@/components/EditPageHeader';
import MemberRegisterForm from './MemberRegisterForm';
import { MEMBERSHIP_CREATE_IN_MEMBER_REGISTER_FAIL_MESSAGES as MEMBERSHIP_FAIL_MESSAGES } from '@/fail-message-handler';
import ElLoadingButton from '@/components/el-loading-button';
import ChildMembershipForm from './ChildMembershipForm';
import Popover from '@/components/popover';
import { fiflboxApi } from '@/utils/api';
import { computedGetters } from '@/utils/vuex-helper';

export default {
  name: 'MemberRegisterPage',
  components: {
    Popover,
    ChildMembershipForm,
    ElLoadingButton,
    MemberRegisterForm,
    EditPageHeader
  },
  data() {
    return {
      isLoading: false,
      isPolicyChecked: false,
      isSubmitMembershipForm: true,
      registerForm: {
        name: '',
        birth: '',
        gender: '1',
        phone_number: '',
        email: ''
      },
      childMembershipForm: {
        parent_membership: null,
        parent_courses: [],
        start: null,
        end: null,
        duration: null,
        enrollment: null,
        consumed: 0,
        balance: null,
        payment: {
          classification: 1,
          cash: 0,
          card: 0,
          remainder: 0,
          paid_at: new Date().toDateFormat()
        }
      }
    };
  },
  watch: {
    isCreateMemberMenuVisible: {
      immediate: true,
      handler: function(isAccessAllowed) {
        if (!isAccessAllowed) {
          this.$router.push('/announce/access-block').catch(() => {});
        }
      }
    }
  },
  computed: {
    ...computedGetters(['isCreateMemberMenuVisible', 'centerAccount'])
  },
  methods: {
    async confirmDialog() {
      try {
        if (this.isSubmitMembershipForm) {
          await this.$confirm(
            `${this.registerForm.name}님을 회원으로 등록하시겠습니까?`,
            '회원 등록'
          );
        } else {
          await this.$confirm(
            '본 회원을 승인 대기 상태로 등록하시겠습니까?',
            '회원 등록'
          );
        }

        return true;
      } catch (err) {
        return false;
      }
    },
    async alertPolicyCheck() {
      if (this.isPolicyChecked) {
        return true;
      }

      await this.$alert(
        '피플박스 서비스 이용 약관 동의가 필요합니다.',
        '회원 등록 실패'
      );

      return false;
    },
    async handleClickRegisterForm() {
      this.isLoading = true;

      if (this.isSubmitMembershipForm) {
        await this.submitRegisterFormWithChildMembership();
        if (this.centerAccount.plan === 0) {
          await this.$store.dispatch('syncCenterAccount');
        }
      } else {
        await this.submitRegisterForm();
      }

      this.isLoading = false;
    },

    async createMemberAccount(registerForm) {
      try {
        let { data } = await fiflboxApi.v2.postMembers(registerForm);

        return {
          isCreated: true,
          data
        };
      } catch (err) {
        return {
          isCreated: false,
          err
        };
      }
    },
    async handleCreatingChildMembershipError({ response }) {
      if (MEMBERSHIP_FAIL_MESSAGES.error_code[response.data.error_code]) {
        return MEMBERSHIP_FAIL_MESSAGES.error_code[
          response.data.error_code
        ].handler();
      }

      if (MEMBERSHIP_FAIL_MESSAGES.status[response.status]) {
        return MEMBERSHIP_FAIL_MESSAGES.status[response.status].handler();
      }

      return MEMBERSHIP_FAIL_MESSAGES.unknown.handler();
    },
    async createChildMembership(enrollmentId) {
      this.childMembershipForm.enrollment = enrollmentId;

      const { isSuccess, error } = await this.$store.dispatch(
        'createChildMembership',
        {
          childMembershipForm: this.childMembershipForm
        }
      );

      if (!isSuccess) {
        this.handleCreatingChildMembershipError(error);
        this.clearAllData();
        return false;
      }

      return true;
    },

    clearAllData() {
      this.$refs.registerForm.resetFields();

      this.registerForm = {
        name: '',
        birth: '',
        gender: '1',
        phone_number: '',
        email: ''
      };

      this.childMembershipForm = {
        parent_membership: null,
        parent_courses: [],
        start: null,
        end: null,
        duration: null,
        enrollment: null,
        consumed: 0,
        balance: null,
        payment: {
          classification: 1,
          cash: 0,
          card: 0,
          remainder: 0,
          paid_at: new Date().toDateFormat()
        }
      };
    },
    async submitRegisterForm() {
      const isPolicyChecked = await this.alertPolicyCheck();

      if (!isPolicyChecked) {
        return;
      }

      const isRegisterFormValid = await this.$refs.registerForm.validate();

      if (!isRegisterFormValid) {
        return this.$alert(
          '회원의 기본 정보를 다시 확인해주세요.',
          '회원 등록 실패'
        );
      }

      const isConfirmed = await this.confirmDialog();

      if (!isConfirmed) {
        return;
      }

      let memberRegisterResult = await this.createMemberAccount(
        this.registerForm
      );

      if (!memberRegisterResult.isCreated) {
        if (memberRegisterResult.err.response.data.error_code == '100') {
          return this.$alert(
            '생년월일을 포함한 회원의 기본 정보를 정확하게 입력해주세요.',
            '회원 등록 실패'
          );
        }

        return this.$message('회원 등록에 실패했습니다.');
      }

      this.$message('승인 대기 회원으로 등록됐습니다.');
      return this.clearAllData();
    },
    async submitRegisterFormWithChildMembership() {
      /**
       * 1. 회원가입 폼 벨리데이트
       * 2. (회원권 폼 벨리데이트)
       * 3. confirm 창
       * 4. 회원가입 리퀘스트 -> 에러시 메세지 후 종료
       * 5. 회원권 리퀘스트 -> 에러시 메세지(회원가입은 성공적이나 회원권은 실패했다)
       */

      const isPolicyChecked = await this.alertPolicyCheck();

      if (!isPolicyChecked) {
        return;
      }

      const isRegisterFormValid = await this.$refs.registerForm.validate();

      if (!isRegisterFormValid) {
        return;
      }

      const childMembershipFormValid = this.$refs.childMembershipForm.validate();

      if (!childMembershipFormValid.isValid) {
        return await this.$alert(
          '회원권의 모든 정보를 정확하게 입력해주세요.',
          '회원 등록 실패'
        );
      }

      const isConfirmed = await this.confirmDialog();

      if (!isConfirmed) {
        return;
      }

      let memberRegisterResult = await this.createMemberAccount(
        this.registerForm
      );

      if (!memberRegisterResult.isCreated) {
        return this.$message('회원 등록에 실패했습니다.');
      }

      let isChildMembershipCreated = await this.createChildMembership(
        memberRegisterResult.data.id
      );

      if (!isChildMembershipCreated) {
        return;
      }

      this.$message('회원으로 등록됐습니다.');
      return this.clearAllData();
    }
  }
};
</script>

<style lang="scss">
.member-register {
  .card-box {
    padding: 30px 45px;
  }

  h2 {
    margin-bottom: 40px;
    line-height: 24px;
    font-size: 16px;
    font-weight: 500;
    letter-spacing: -0.5px;
    text-align: left;
    flex: auto;
  }

  .el-form {
    width: 100%;
  }

  .policy-check {
    margin-top: 70px;
    color: #6c6c6c;
    width: 100%;
    .el-checkbox__label {
      max-width: initial;
    }
    a {
      color: #6c6c6c;
      text-decoration: #6c6c6c underline;
    }
  }

  .el-input.is-disabled .el-input__inner {
    color: #9b9b9b;
    background-color: #f2f2f2;
  }

  .membership-form-switch {
    span {
      font-size: 12px;
      font-weight: 500;
      font-style: normal;
      font-stretch: normal;
      line-height: 1.83;
      letter-spacing: -0.6px;
      text-align: right;
      color: #4a4a4a;
    }
  }
}
</style>
